import React, { useMemo } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  addDays,
  subDays,
  getYear,
  isSameMonth,
  isSameWeek,
  isSameDay,
} from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/Button';

const WEEK_STARTS_ON = 1 as const; // Monday

export function ScheduleFiltersAndCount({
  filterSlot,
  lessonCount,
  className,
}: {
  filterSlot?: React.ReactNode;
  lessonCount?: { filtered: number; total: number };
  className?: string;
}) {
  if (!filterSlot && lessonCount == null) return null;

  const countLabel =
    lessonCount != null
      ? `${lessonCount.filtered} of ${lessonCount.total} lesson${lessonCount.total !== 1 ? 's' : ''}`
      : null;

  return (
    <div className={`flex h-8 shrink-0 items-center gap-1.5 sm:gap-2 ${className ?? ''}`}>
      {countLabel ? (
        <span className="hidden shrink-0 items-center whitespace-nowrap text-[10px] leading-none text-muted-foreground sm:inline-flex sm:text-xs">
          {countLabel}
        </span>
      ) : null}
      {filterSlot}
    </div>
  );
}

/** Secondary toolbar for lesson calendar — filters, counts, date navigation */
export function LessonScheduleSubheaderToolbar({
  filterSlot,
  lessonCount,
  calendarNav,
}: {
  filterSlot?: React.ReactNode;
  lessonCount?: { filtered: number; total: number };
  calendarNav?: React.ReactNode;
}) {
  return (
    <div className="flex w-full min-w-0 flex-row items-center gap-2 sm:gap-3">
      {calendarNav ? (
        <div className="flex min-w-0 flex-1 items-center justify-start overflow-x-auto">
          {calendarNav}
        </div>
      ) : null}
      <ScheduleFiltersAndCount
        filterSlot={filterSlot}
        lessonCount={lessonCount}
        className={calendarNav ? 'shrink-0' : 'ml-auto'}
      />
    </div>
  );
}

export function LessonCalendarMonthNav({
  currentMonth,
  onCurrentMonthChange,
  onMonthChange,
}: {
  currentMonth: Date;
  onCurrentMonthChange: (date: Date) => void;
  /** Optional hook for data fetching when month changes (year, month 1–12) */
  onMonthChange?: (year: number, month: number) => void;
}) {
  const displayYear = getYear(currentMonth);
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);

  const years = useMemo(() => {
    const y: number[] = [];
    const current = getYear(new Date());
    for (let i = current - 2; i <= current + 5; i++) {
      y.push(i);
    }
    return y;
  }, []);

  const canPrev = monthStart > startOfMonth(new Date(displayYear, 0, 1));
  const canNext = monthEnd < endOfMonth(new Date(displayYear, 11, 31));

  const navigate = (newDate: Date) => {
    onCurrentMonthChange(newDate);
    onMonthChange?.(newDate.getFullYear(), newDate.getMonth());
  };

  const isCurrentMonth = isSameMonth(currentMonth, new Date());

  return (
    <div className="inline-flex shrink-0 items-center gap-2">
      <div
        className="inline-flex shrink-0 items-center overflow-hidden rounded-lg border border-border bg-background shadow-sm"
        role="group"
        aria-label="Calendar month navigation"
      >
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 rounded-none"
          onClick={() => navigate(subMonths(currentMonth, 1))}
          disabled={!canPrev}
          aria-label="Previous month"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="flex h-8 w-[11.5rem] shrink-0 items-center gap-1 border-x border-border pl-2 pr-2.5 sm:w-[13.5rem] sm:gap-1.5 sm:px-2.5">
          <span
            className="inline-flex w-[4.75rem] shrink-0 items-center justify-center text-sm font-semibold leading-none sm:w-[6.5rem]"
            aria-live="polite"
          >
            <span className="sm:hidden">{format(currentMonth, 'MMM')}</span>
            <span className="hidden sm:inline">{format(currentMonth, 'MMMM')}</span>
          </span>
          <select
            aria-label="Calendar year"
            value={displayYear.toString()}
            onChange={(e) => {
              const year = parseInt(e.target.value, 10);
              navigate(new Date(year, currentMonth.getMonth(), 1));
            }}
            className="h-7 w-[5.5rem] shrink-0 border-0 bg-transparent py-0 pl-0 pr-9 text-xs tabular-nums shadow-none focus-visible:ring-0 sm:w-[5.75rem] sm:text-sm !bg-[length:1rem_1rem] ![background-position:right_0.75rem_center]"
          >
            {years.map((year) => (
              <option key={year} value={year.toString()}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 rounded-none"
          onClick={() => navigate(addMonths(currentMonth, 1))}
          disabled={!canNext}
          aria-label="Next month"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-8 shrink-0 px-2.5"
        onClick={() => navigate(startOfMonth(new Date()))}
        disabled={isCurrentMonth}
        aria-label="Go to this month"
      >
        Today
      </Button>
    </div>
  );
}

export function ScheduleWeekNav({
  weekStart,
  onWeekStartChange,
  onWeekChange,
}: {
  weekStart: Date;
  onWeekStartChange: (date: Date) => void;
  onWeekChange?: (weekStart: Date) => void;
}) {
  const weekEnd = addDays(weekStart, 6);

  const navigate = (newWeek: Date) => {
    onWeekStartChange(newWeek);
    onWeekChange?.(newWeek);
  };

  const isCurrentWeek = isSameWeek(weekStart, new Date(), { weekStartsOn: WEEK_STARTS_ON });

  return (
    <div className="inline-flex shrink-0 items-center gap-2">
      <div
        className="inline-flex shrink-0 items-center overflow-hidden rounded-lg border border-border bg-background shadow-sm"
        role="group"
        aria-label="Calendar week navigation"
      >
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 rounded-none"
          onClick={() => navigate(subWeeks(weekStart, 1))}
          aria-label="Previous week"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="flex h-8 shrink-0 items-center border-x border-border px-2 sm:px-2.5">
          <span
            className="inline-flex shrink-0 items-center justify-center whitespace-nowrap text-xs font-semibold leading-none sm:text-sm"
            aria-live="polite"
          >
            <span className="sm:hidden">
              {format(weekStart, 'MMM d')} – {format(weekEnd, 'MMM d')}
            </span>
            <span className="hidden sm:inline">
              {format(weekStart, 'MMM d')} – {format(weekEnd, 'MMM d, yyyy')}
            </span>
          </span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 rounded-none"
          onClick={() => navigate(addWeeks(weekStart, 1))}
          aria-label="Next week"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-8 shrink-0 px-2.5"
        onClick={() => navigate(startOfWeek(new Date(), { weekStartsOn: WEEK_STARTS_ON }))}
        disabled={isCurrentWeek}
        aria-label="Go to this week"
      >
        Today
      </Button>
    </div>
  );
}

export function ScheduleDayNav({
  selectedDate,
  onDateChange,
}: {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
}) {
  const navigate = (newDate: Date) => {
    onDateChange(newDate);
  };

  const isToday = isSameDay(selectedDate, new Date());

  return (
    <div className="inline-flex min-w-0 shrink-0 items-center gap-2">
      <div
        className="inline-flex shrink-0 items-center overflow-hidden rounded-lg border border-border bg-background shadow-sm"
        role="group"
        aria-label="Calendar day navigation"
      >
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 rounded-none"
          onClick={() => navigate(subDays(selectedDate, 1))}
          aria-label="Previous day"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="flex h-8 min-w-0 shrink-0 items-center border-x border-border px-2 sm:px-2.5">
          <span
            className="inline-flex min-w-0 shrink-0 items-center justify-center whitespace-nowrap text-xs font-semibold leading-none sm:text-sm"
            aria-live="polite"
          >
            <span className="sm:hidden">{format(selectedDate, 'EEE, MMM d')}</span>
            <span className="hidden sm:inline">{format(selectedDate, 'EEEE, MMM d, yyyy')}</span>
          </span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 rounded-none"
          onClick={() => navigate(addDays(selectedDate, 1))}
          aria-label="Next day"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-8 shrink-0 px-2.5"
        onClick={() => navigate(new Date())}
        disabled={isToday}
        aria-label="Go to today"
      >
        Today
      </Button>
    </div>
  );
}
